import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SideMenuLayout } from '../../components/Layout/SideMenuLayout';
import { OnboardingColumn } from './OnboardingColumn/OnboardingColumn';
import { PendingLocationsColumn } from './PendingLocationsColumn/PendingLocationsColumn';
import { RadioGroup, RadioGroupOption } from '../../components/ui/RadioGroup/RadioGroup';
import { RadioChangeEvent } from 'antd';
import { LocationOnboardingInfo, useLocationsInOnboardingQuery } from '../../graphql/query/locationsInOnboarding';
import { Spacer } from '../../components/ui/Spacer';
import { FilterByNumber } from '../../components/ui/Filters';
import { Tabs } from 'antd';
import { useQuery } from '@apollo/client';
import { PENDING_HUBSPOT_DEALS, PendingHubspotDealsResponse } from '../../graphql/query/pendingHubspotDeals';

const { TabPane } = Tabs;

const MenuWrapper = styled.div`
  padding: 16px 16px;
  width: 100%;
`;

const FilterWrapper = styled.div`
  align-items: left;
  text-align: left;
`;

enum AccountType {
  ALL = 'ALL',
  PARTNER = 'Partner',
}

export enum LocationOnboardingStage {
  AccountSetup = 'Account Setup',
  UserSetup = 'User Setup & Configuration',
  InventorySetup = 'Inventory Setup',
  OnboardingConfirmation = 'Confirm Onboarding Completed',
  OnboardingCompleted = 'Onboarding Completed',
}

const accountTypeOptions: RadioGroupOption[] = [
  { label: 'All', value: AccountType.ALL },
  { label: 'Partner', value: AccountType.PARTNER },
];

const PageContent = styled.div`
  padding: 10px 10px;
  width: 100%;
  align-items: flex-start;
`;

const OnboardingDashboard: React.FC = () => {
  const [accountType, setAccountType] = useState<AccountType>(AccountType.ALL);
  const { locationsInOnboarding: locations, refetch, error } = useLocationsInOnboardingQuery({});
  const [filters, setFilters] = useState<LocationOnboardingStage[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<LocationOnboardingInfo[]>([]);
  const { data: pendingDealsData, refetch: refetchPendingDeals } =
    useQuery<PendingHubspotDealsResponse>(PENDING_HUBSPOT_DEALS);

  useEffect(() => {
    if (locations.length && filters.length) {
      const filteredLocations = locations.filter((location) =>
        filters.includes(location.onboardingStage as LocationOnboardingStage)
      );
      setFilteredLocations(filteredLocations);
    } else if (!error) {
      setFilteredLocations(locations);
    }
  }, [filters, locations, error]);

  const handleChangeCondition = ({ target: { value } }: RadioChangeEvent) => {
    setAccountType(value);
    if (value === AccountType.PARTNER) {
      refetch({ partnersOnly: true });
    } else {
      refetch({ partnersOnly: false });
    }
  };

  const handleSearch = useCallback(
    (search: string) => {
      if (search) {
        refetch({
          partnersOnly: AccountType.PARTNER === accountType,
          search,
        });
      }
    },
    [accountType, refetch]
  );

  return (
    <PageContent>
      <Tabs defaultActiveKey="locations">
        <TabPane tab="Locations" key="locations">
          <SideMenuLayout
            title="Onboarding"
            sideColumn={
              <MenuWrapper>
                <RadioGroup
                  size="middle"
                  defaultValue={accountType}
                  onChange={handleChangeCondition}
                  options={accountTypeOptions}
                ></RadioGroup>
                <Spacer type="stack" size="s" />
                <FilterWrapper>
                  <FilterByNumber
                    title="Onboarding Stage"
                    selectedItems={filters}
                    options={[
                      { id: LocationOnboardingStage.AccountSetup, name: LocationOnboardingStage.AccountSetup },
                      { id: LocationOnboardingStage.UserSetup, name: LocationOnboardingStage.UserSetup },
                      { id: LocationOnboardingStage.InventorySetup, name: LocationOnboardingStage.InventorySetup },
                      {
                        id: LocationOnboardingStage.OnboardingConfirmation,
                        name: LocationOnboardingStage.OnboardingConfirmation,
                      },
                    ]}
                    onChange={(checkedValue) => setFilters(checkedValue as LocationOnboardingStage[])}
                  />
                </FilterWrapper>
              </MenuWrapper>
            }
            contentColumn={<OnboardingColumn locations={filteredLocations} handleSearch={handleSearch} />}
          />
        </TabPane>
        <TabPane
          tab={`Pending Locations ${`(${pendingDealsData?.pendingHubspotDeals.length || 0})`}`}
          key="pendingLocations"
        >
          <PendingLocationsColumn pendingDealsData={pendingDealsData} refetchPendingDeals={refetchPendingDeals} />
        </TabPane>
      </Tabs>
    </PageContent>
  );
};

export default OnboardingDashboard;
